import APPOINTMENTDEFAULTIMAGE from "../assets/Appointments.png";
import LISTINGSIMAGE from "../assets/Overview.jpg";
import mediaType from "../Constants/mediaType.js";
import CustomCarousel from "../Path/Carousel/CustomCarousel.jsx";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import SocialMediaPreview from "./SocialMediaPreview/SocialMediaPreview.jsx";
import { renderPdfAllPagesToDataUrl } from "../Services/pdfToImage.js";
import { cachePdfUrl, getCachedPdfData, cachePdfData } from "../Services/pdfCache.js";

const Loader = () => <div className="w-8 h-8 rounded-full border-2 border-gray-500 border-t-transparent animate-spin" />

const ListingPopUpPreview = ({ listing, setListingPopup }) => {
  const validMedia = (listing.media || []).filter(
    (item) => item.signedUrl !== null
  );
  const pdfMedia = validMedia.find((item) => item.mediaType === mediaType.PDF);
  const carouselMedia = validMedia.filter(
    (item) => item.mediaType === mediaType.IMAGE
  );
  
  const [showMedia, setShowMedia] = useState(false);
  const [pdfFailed, setPdfFailed] = useState(false);
  const [pdfImageUrl, setPdfImageUrl] = useState([]);
  const [isConvertingPdfToImage, setIsConvertingPdfToImage] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const { t } = useTranslation();
  const isShowInstaPopUp = listing.source === 2;

  useEffect(() => {
    let isMounted = true;
    if (pdfMedia && pdfMedia?.signedUrl) {
      setIsConvertingPdfToImage(true);
      // Cache the mapping of listingId -> pdfUrl on first detection
      const listingId = listing?.id ?? listing?.listingId ?? listing?._id;
      cachePdfUrl(listingId, pdfMedia?.signedUrl);

      // Try cache first: if images + totalPages already present, use and skip API
      const cached = getCachedPdfData(listingId);

      if (cached && cached.images && cached.images.length > 0) {
        setPdfImageUrl(cached.images);
        setTotalPages(cached.totalPages || cached.images.length);
        setIsConvertingPdfToImage(false);
      } else {
        setIsConvertingPdfToImage(true);
        setPdfFailed(false);
        setPdfImageUrl([]);
        (async () => {
          try {
            const result = await renderPdfAllPagesToDataUrl(pdfMedia?.signedUrl);
            if (isMounted) {
              setPdfImageUrl(result.allPagesImages); // Store all pages as array
              setTotalPages(result.totalPages);
              // Write to cache for subsequent opens
              cachePdfData(listingId, pdfMedia?.signedUrl, result.allPagesImages, result.totalPages);
            }

          } catch (err) {
            console.error(err);
            if (isMounted) setPdfFailed(true);
          } finally {
            setIsConvertingPdfToImage(false);
          }
        })();
      }

    }
    return () => {
      isMounted = false;
    };
  }, [pdfMedia, listing]);

  if (pdfMedia) {
    return (
      <div className="fixed inset-0 pt-10 z-50 bg-black/20 backdrop-blur-sm flex justify-center">
        <div
          className="bg-white text-black rounded-lg shadow-xl max-w-[50%] w-full relative overflow-y-auto h-[70%]"
          onClick={(e) => e.stopPropagation()}
        >

          <button
            onClick={(e) => {
              e.stopPropagation();
              setListingPopup(null);
            }}
            className="absolute top-2 right-2 text-gray-500 hover:text-red-500 text-xl 3xl:text-[4rem] z-10"
          >
            &times;
          </button>
          <div className="w-full h-1/2 flex items-center justify-center overflow-hidden">
            {
              isConvertingPdfToImage ?
                <div className="w-full h-full flex items-center justify-center">
                  <Loader />
                </div> :
                (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="relative rounded-lg" style={{ width: '80%', maxWidth: '480px' }}>
                      <img
                        alt="PDF preview"
                        src={
                          (pdfImageUrl?.length && pdfImageUrl[0]) || LISTINGSIMAGE
                        }
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = listing.appointmentId
                            ? APPOINTMENTDEFAULTIMAGE
                            : LISTINGSIMAGE;
                        }}
                        style={{ width: '100%', height: 'auto', imageRendering: 'auto' }}
                        className="rounded-lg"
                      />
                    </div>
                  </div>
                )


            }
          </div>
          <h2 className="text-black text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl 2xl:text-4xl font-bold p-2 sm:p-3 md:p-4 leading-tight break-words">
            {listing?.title}
          </h2>
          <p
            className="text-black text-sm 2xl:text-[1rem] p-4"
            dangerouslySetInnerHTML={{ __html: listing?.description }}
          ></p>
          {validMedia.length > 0 ? (
            <button
              className="text-black text-sm border border-gray-400 px-4 py-2 rounded ml-4 bg-gray-50"
              onClick={() => setShowMedia(!showMedia)}
            >
              {!showMedia ? t("show_media") : t("hide_media")}
            </button>
          ) : null}

          {showMedia && (
            <div className="w-full flex justify-center mb-6 mt-2">
              {isConvertingPdfToImage ?
                <div className="w-full h-full flex items-center justify-center">
                  <Loader />
                </div> : !!pdfImageUrl?.length && <CustomCarousel imageList={pdfImageUrl} isImageAsBlob />
              }
            </div>
          )}


        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 pt-1 z-50 bg-black/20 backdrop-blur-sm flex justify-center">
      <div
        className="bg-white text-black rounded-lg shadow-xl max-w-[50%] w-full relative overflow-y-auto h-[70%]"
        onClick={(e) => e.stopPropagation()}
      >
        {isShowInstaPopUp ? (
          <SocialMediaPreview url={listing.website} />
        ) : (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setListingPopup(null);
              }}
              className="absolute top-2 right-2 text-gray-500 hover:text-red-500 text-xl 3xl:text-[4rem] z-10"
            >
              &times;
            </button>
            <div className="w-full h-1/2  flex items-center justify-center overflow-hidden">
              <img
                alt="Listing"
                src={
                  listing?.media?.find(
                    (m) => m?.mediaType === mediaType?.IMAGE && m?.signedUrl
                  )?.signedUrl || APPOINTMENTDEFAULTIMAGE
                }
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = listing.appointmentId
                    ? APPOINTMENTDEFAULTIMAGE
                    : LISTINGSIMAGE;
                }}
                // className="max-w-full max-h-full "
                style={{ imageRendering: 'auto' }}
              />
            </div>
            {/* <img
              alt="Listing"
              className={`object-cover object-center h-[50%] w-full rounded-t-lg transition-opacity duration-500`}
              src={
                listing?.media?.find(
                  (m) => m?.mediaType === mediaType?.IMAGE && m?.signedUrl
                )?.signedUrl || APPOINTMENTDEFAULTIMAGE
              }
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = listing.appointmentId
                  ? APPOINTMENTDEFAULTIMAGE
                  : LISTINGSIMAGE;
              }}
            /> */}
            {/* <h2 className="text-black text-2xl font-bold 2xl:text-[3rem] p-4"> */}
            <h2 className="text-black text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl 2xl:text-4xl font-bold p-2 sm:p-3 md:p-4 leading-tight break-words">
              {listing?.title}
            </h2>
            <p
              className="text-black text-sm 2xl:text-[1rem] p-4"
              dangerouslySetInnerHTML={{ __html: listing?.description }}
            ></p>
            {validMedia.length > 0 ? (
              <button
                className="text-black text-sm border border-gray-400 px-4 py-2 rounded ml-4 bg-gray-50"
                onClick={() => setShowMedia(!showMedia)}
              >
                {!showMedia ? t("show_media") : t("hide_media")}
              </button>
            ) : null}

            {showMedia ? (
              <div className="w-full flex justify-center mb-6 mt-2">
                {!!pdfImageUrl?.length ? <CustomCarousel imageList={pdfImageUrl} isImageAsBlob /> : validMedia.length > 0 ? (
                  <div className="w-full">
                    {/**
                     * pdfMedia ? (
                      <iframe
                        src={pdfMedia.signedUrl}
                        width="100%"
                        height="500px"
                        className="rounded-lg shadow-md"
                        title="PDF Viewer"
                      ></iframe>
                    ) :
                     */}
                    {carouselMedia.length > 0 ? (
                      <CustomCarousel
                        imageList={carouselMedia}
                        sourceId={listing?.sourceId}
                        appointmentId={listing.appointmentId || null}
                      />
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};
export default ListingPopUpPreview;
